import axios from 'axios';
import ApiError from './ApiError';
import { API_BASE, STORAGE_KEYS } from '../../config/env';
export const UNAUTHORIZED_EVENT = 'carbon:unauthorized';
export const FORBIDDEN_EVENT = 'carbon:forbidden';

function getToken() {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEYS.auth) ||
      sessionStorage.getItem(STORAGE_KEYS.auth);
    return raw ? (JSON.parse(raw)?.token ?? null) : null;
  } catch {
    return null;
  }
}

function clearStoredAuth() {
  try {
    localStorage.removeItem(STORAGE_KEYS.auth);
    sessionStorage.removeItem(STORAGE_KEYS.auth);
  } catch { /* ignore */ }
}

const STATUS_KIND = Object.freeze({
  400: 'badRequest',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  // One-release compatibility with the previous contract.
  413: 'fileTooLarge',
  422: 'validation',
  500: 'server',
  502: 'serviceUnavailable',
  503: 'serviceUnavailable',
  504: 'serviceUnavailable',
});

const asObject = value => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const asParams = value => {
  const source = asObject(value);
  return Object.fromEntries(
    Object.entries(source).filter(([, item]) => (
      item === null || ['string', 'number', 'boolean'].includes(typeof item)
    )),
  );
};

const asMessage = value => (
  typeof value === 'string' && value.trim() ? value.trim() : ''
);

const fieldNameFromLocation = location => (
  Array.isArray(location)
    ? location.filter(part => part !== 'body').join('.')
    : String(location || '')
);

const normalizeFieldErrors = (detail, envelope) => {
  const source = Array.isArray(envelope.field_errors)
    ? envelope.field_errors
    : (Array.isArray(detail) ? detail : []);

  return source.map(item => {
    const legacyToken = String(item?.code || item?.type || 'FIELD_INVALID')
      .toUpperCase()
      .replace(/[.-]/g, '_');
    return {
      field: String(item?.field || item?.path || fieldNameFromLocation(item?.loc)),
      message: asMessage(item?.message || item?.msg) || legacyToken,
      params: asParams(item?.params || item?.ctx),
    };
  });
};

const parseRetryAfter = (headers, params) => {
  const value = headers?.['retry-after'] ?? headers?.get?.('retry-after')
    ?? params.retry_after ?? params.retryAfter;
  if (value === undefined || value === null || value === '') return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) ? seconds : String(value);
};

/** Normalise current and legacy error shapes into the shared safe contract. */
export function extractErrorEnvelope(status, data, headers = {}) {
  const response = asObject(data);
  const detail = response.detail;
  const envelope = asObject(detail);
  const params = asParams(envelope.params || response.params);
  const message = asMessage(
    envelope.message
    || response.message
    || (typeof detail === 'string' ? detail : ''),
  );

  const fieldErrors = normalizeFieldErrors(detail, envelope);
  const statusKind = STATUS_KIND[status] || (status >= 500 ? 'server' : 'client');

  return {
    status,
    message,
    params,
    fieldErrors,
    kind: status === 400 && fieldErrors.length > 0 ? 'validation' : statusKind,
    retryAfter: parseRetryAfter(headers, params),
  };
}

// Backward-compatible named export for callers that normalize without using
// the interceptor.
export function extractMessage(status, data) {
  return extractErrorEnvelope(status, data).message;
}

const axiosClient = axios.create({
  baseURL: API_BASE,
  timeout: 300000,
  headers: { 'Content-Type': 'application/json' },
  // FastAPI List[str] params need ?k=a&k=b — axios 1.x default produces ?k[]=a&k[]=b
  // which FastAPI silently ignores (assigns null, returns unfiltered data).
  paramsSerializer: { indexes: null },
});

// ── Request interceptor ─────────────────────────────────────────────────────
// Reads token fresh from storage on every request — never cached.
axiosClient.interceptors.request.use(config => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor ────────────────────────────────────────────────────
axiosClient.interceptors.response.use(
  response => response,

  async error => {
    // BRANCH 0 — AbortController cancelled the request
    // Pass the raw CanceledError through; callers check axios.isCancel(err).
    if (axios.isCancel(error)) return Promise.reject(error);

    // BRANCH 1 — No response: network loss, CORS block, or timeout
    if (!error.response) {
      const isTimeout = error.code === 'ECONNABORTED';
      return Promise.reject(new ApiError(
        isTimeout ? 'REQUEST_TIMEOUT' : 'NETWORK_UNAVAILABLE',
        {
          status: 0,
          kind: isTimeout ? 'timeout' : 'network',
          cause: error,
        },
      ));
    }

    const { status } = error.response;
    let { data } = error.response;

    // responseType:'blob' makes the error body a Blob too — re-parse to JSON
    if (data instanceof Blob) {
      try { data = JSON.parse(await data.text()); } catch { data = null; }
    }

    const normalized = extractErrorEnvelope(status, data, error.response.headers);

    // BRANCH 2 — 401 on a normal (post-login) endpoint: session expired
    // Pre-auth endpoints (login, sso-login) opt out with { skipAuthRedirect: true }.
    if (status === 401 && !error.config?.skipAuthRedirect) {
      clearStoredAuth();
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
      return Promise.reject(
        new ApiError(normalized.message, {
          ...normalized,
          cause: error,
        }),
      );
    }

    // Authenticated API permission failures have one surface: the permission page.
    if (status === 403 && !error.config?.skipAuthRedirect) {
      window.dispatchEvent(new Event(FORBIDDEN_EVENT));
    }

    // BRANCH 3 — all other HTTP errors
    return Promise.reject(
      new ApiError(normalized.message, {
        ...normalized,
        cause: error,
      }),
    );
  },
);

export default axiosClient;
